/**
 * Précisions apportées par l'auteur après lecture des changements annoncés.
 *
 * Quand l'IA signale qu'une application ne fonctionnera pas exactement comme
 * avant, l'auteur n'avait que deux réponses : accepter, ou tout abandonner. C'est
 * un choix pauvre — les changements annoncés sont souvent *presque* bons, et ce
 * qu'il faudrait dire tient en une phrase : « l'objectif de la jauge, c'est 15,
 * pas 20 ».
 *
 * Cette troisième voie relance la transformation avec la remarque en plus. Deux
 * propriétés y tiennent :
 *
 * 1. **Les précisions s'accumulent** au lieu de se remplacer. Le modèle voit la
 *    demande d'origine puis toutes les remarques, dans l'ordre où elles ont été
 *    faites : sans quoi une deuxième précision ferait oublier la première, et
 *    l'auteur tournerait en rond.
 * 2. **Le numéro de version ne bouge pas.** La reprise se fait sur la même ligne
 *    de déploiement, rien n'ayant été construit ni mis en ligne. Créer une version
 *    à chaque échange ferait arriver un premier déploiement en v6.
 */
/**
 * Introduit chaque remarque, pour que le modèle sache d'où elle vient.
 *
 * Exporté parce que le prompt de transformation le cite : les deux doivent dire
 * exactement la même chose, à l'apostrophe près, et un test le vérifie. Une
 * divergence ferait décrire au prompt un marqueur que le modèle ne voit pas.
 */
export const REFINEMENT_MARKER = 'Précision de l’auteur, après lecture des changements annoncés :';
/**
 * Ajoute une précision à la demande transmise au modèle.
 *
 * @param existing demande courante — description initiale, précédentes
 *   précisions, ou `null` pour un artifact collé sans autre consigne
 */
export function appendRefinement(existing, precisions) {
    const bloc = `${REFINEMENT_MARKER}\n${precisions.trim()}`;
    const base = (existing ?? '').trim();
    return base.length > 0 ? `${base}\n\n${bloc}` : bloc;
}
/**
 * La demande porte-t-elle une description initiale, et pas seulement des
 * précisions ?
 *
 * C'est ce qui distingue une application **écrite d'après une description** d'un
 * artifact collé auquel l'auteur a ensuite demandé une correction : les deux
 * finissent avec une `modificationInstruction` non vide, et rien d'autre ne les
 * sépare une fois la transformation passée — le code produit ayant alors rempli
 * `originalCode` dans les deux cas.
 *
 * Le critère est la présence de texte **avant** la première précision :
 * `appendRefinement(null, …)` commence par le marqueur, `appendRefinement(description, …)`
 * non.
 */
export function hasInitialDescription(instruction) {
    if (!instruction)
        return false;
    const [avant] = instruction.split(REFINEMENT_MARKER);
    return (avant ?? '').trim().length > 0;
}
/** Nombre de précisions déjà accumulées, pour l'afficher à l'auteur. */
export function countRefinements(instruction) {
    if (!instruction)
        return 0;
    return instruction.split(REFINEMENT_MARKER).length - 1;
}
//# sourceMappingURL=refine.js.map